import React, { createContext, useState, useEffect, useContext, ReactNode, useCallback } from 'react';
import { createApiClient, API_BASE_URL } from '../config/api';

// Create API client for auth operations
const apiClient = createApiClient();

// Types
interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  aliasName?: string;
  googleProfilePictureUrl?: string;
  role: string;
  subscriptionTier: string;
  emailPreferences?: {
    newsletter?: boolean;
    notifyOnInspiration?: boolean;
    reminders?: string;
    unsubscribed?: boolean;
  };
  location?: {
    city?: string;
    state?: string;
    zipcode?: string;
  };
}

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  error: string | null;
  login: () => void;
  logout: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
  updateUserProfile: (userData: User) => void;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  error: null,
  login: () => {},
  logout: async () => {},
  checkAuthStatus: async () => {},
  updateUserProfile: () => {},
});

// Helper to extract token from URL fragment
const getTokenFromFragment = (): string | null => {
  const hash = window.location.hash;
  if (!hash) return null;
  
  const match = hash.match(/token=([^&]*)/);
  return match ? match[1] : null;
};

// Helper to store token in localStorage
const saveToken = (token: string): void => {
  localStorage.setItem('auth_token', token);
  // Set authorization header for all future requests
  apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

// Helper to get token from localStorage
const getToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

// Helper to remove token from localStorage
const removeToken = (): void => {
  localStorage.removeItem('auth_token');
  // Remove authorization header
  delete apiClient.defaults.headers.common['Authorization'];
};

// Set token in axios headers if it exists
const setAuthHeader = (token: string | null): void => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
  }
};

// Provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Check for token in URL fragment (after OAuth redirect)
  useEffect(() => {
    const token = getTokenFromFragment();
    if (token) {
      console.log('Found token in URL fragment, saving it');
      saveToken(token);
      // Remove token from URL to prevent accidental sharing
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Set up axios interceptor to include token in requests
  useEffect(() => {
    const token = getToken();
    if (token) {
      setAuthHeader(token);
    }
  }, []);

  // Check authentication status
  const checkAuthStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = getToken();
      if (!token) {
        setCurrentUser(null);
        setLoading(false);
        return;
      }

      // Set the auth header for this request
      setAuthHeader(token);
      
      // Get auth status from API using JWT token
      const response = await apiClient.get('/auth/status');
      
      // Add type assertion to response.data
      const data = response.data as { authenticated: boolean; user?: User };
      
      if (data.authenticated) {
        setCurrentUser(data.user || null);
      } else {
        setCurrentUser(null);
        // If not authenticated, remove invalid token
        removeToken();
      }
    } catch (err) {
      console.error('Error checking authentication status:', err);
      setCurrentUser(null);
      // If request fails, likely invalid token, so remove it
      removeToken();
      setError('Failed to check authentication status');
    } finally {
      setLoading(false);
    }
  }, []);

  // Check auth status on initial load
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Login function - redirects to Google OAuth
  const login = (): void => {
    window.location.href = `${API_BASE_URL}/auth/google`;
  };

  // Logout function
  const logout = async (): Promise<void> => {
    try {
      const token = getToken();
      if (token) {
        setAuthHeader(token);
        await apiClient.post('/auth/logout');
      }
      removeToken();
      setCurrentUser(null);
    } catch (err) {
      console.error('Logout error:', err);
      // Still remove token even if logout request fails
      removeToken();
      setCurrentUser(null);
      setError('Failed to logout');
    }
  };

  // Update user profile data
  const updateUserProfile = (userData: User): void => {
    setCurrentUser(userData);
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    logout,
    checkAuthStatus,
    updateUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook for using auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

export default AuthContext; 